import { type KeyboardEvent, type ReactElement, useState } from 'react'
import {
  type ConversationResolution,
  type ConversationResolveLocationFlagged,
  type LocationId,
  isDefined,
  isEmpty,
} from '@terros/common'
import { Checkbox, Radio, Stack } from '@mantine/core'
import { getRecordingLocationWarnings, useLocation, useTranslation, useRecordingReviewWorkflows } from '@front/model'
import { LocationAutocomplete, TAlert, TButton, TRadioCard } from '@components'
import { ReviewLocationWarnings } from './ReviewLocationWarnings'

type Props = {
  busy: boolean
  rejection?: ConversationResolveLocationFlagged
  onSubmit: (resolution: ConversationResolution) => void
}

// The Fix location card contains a button, so its radio container must not be another button.
function selectWithKeyboard(event: KeyboardEvent<HTMLElement>): void {
  if (event.target !== event.currentTarget) return
  if (event.key === ' ' || event.key === 'Enter') {
    event.preventDefault()
    event.currentTarget.click()
  }
}

export function ReviewResolutionForm({ busy, rejection, onSubmit }: Props): ReactElement {
  const { t } = useTranslation()
  const [action, setAction] = useState<'discard' | 'relocate'>()
  const [confirmed, setConfirmed] = useState(false)
  const [locationId, setLocationId] = useState<LocationId>()
  const location = useLocation(locationId)
  const workflows = useRecordingReviewWorkflows()
  const warnings = getRecordingLocationWarnings({
    accounts: location.data?.accounts ?? [],
    workflows: workflows.data ?? [],
  })
  const checked = location.isSuccess && workflows.isSuccess && !location.isFetching && !workflows.isFetching
  const serverMatches = rejection?.matches.filter((match) => match.locationId === locationId) ?? []
  const canRelocate = checked && isEmpty(warnings) && isDefined(locationId) && isEmpty(serverMatches)
  const canSubmit = confirmed && !busy && (action === 'discard' || (action === 'relocate' && canRelocate))

  function submit(): void {
    if (!canSubmit) return
    if (action === 'discard') onSubmit({ action })
    if (action === 'relocate' && isDefined(locationId)) onSubmit({ action, locationId })
  }

  return (
    <Stack gap='sm'>
      <Radio.Group
        label={t('Choose an action')}
        value={action ?? ''}
        onChange={(value) => {
          if (busy) return
          if (value !== 'discard' && value !== 'relocate') return
          if (value === action) return
          setAction(value)
          setConfirmed(false)
        }}
      >
        <Stack gap='sm' mt='xs'>
          <TRadioCard
            value='discard'
            label={t('Discard')}
            aria-label={t('Discard')}
            description={t('Permanently delete this conversation and its audio.')}
            disabled={busy}
          />
          <TRadioCard
            component='div'
            tabIndex={busy ? -1 : 0}
            onKeyDown={selectWithKeyboard}
            value='relocate'
            label={t('Fix location')}
            aria-label={t('Fix location')}
            description={t('Select the correct location and resume processing.')}
            disabled={busy}
          >
            <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              <LocationAutocomplete
                locationId={locationId}
                disabled={busy || action !== 'relocate'}
                canEditDetails={false}
                onLocationChange={(values) => setLocationId(values.locationId)}
              />
            </div>
          </TRadioCard>
        </Stack>
      </Radio.Group>
      {action === 'relocate' && (
        <>
          {isDefined(locationId) && !checked && <TAlert>{t('Checking the selected location…')}</TAlert>}
          {(isDefined(location.error) || isDefined(workflows.error)) && (
            <TAlert variant='error'>{t('Could not check the location. Refresh and try again.')}</TAlert>
          )}
          <ReviewLocationWarnings
            warnings={warnings}
            rejection={rejection}
            locationId={locationId}
            workflows={workflows.data ?? []}
          />
        </>
      )}
      <Checkbox
        required
        label={t('I understand this action cannot be undone.')}
        checked={confirmed}
        onChange={(event) => setConfirmed(event.currentTarget.checked)}
        disabled={busy}
      />
      <TButton
        variant={action === 'discard' ? 'destructive' : 'primary'}
        disabled={!canSubmit}
        loading={busy}
        onClick={submit}
      >
        {t('Confirm')}
      </TButton>
    </Stack>
  )
}
